import { useContext, useRef, useState, useEffect, useCallback } from "react";
import {
  Box, Typography, IconButton, Dialog, DialogTitle, DialogContent,
  CircularProgress, Button, Tabs, Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AuthContext } from "../context/AuthContext";
import Header from "../components/Header";
import { getMediaUrl, getMyMedia, uploadCover, setCoverFromGallery } from "../service/mediaApi";
import { getMyAlbums, getSharedWithMe, getAlbum } from "../service/albumApi";
import { useSnack } from "../hooks/useSnack";

// ─── Grille de miniatures ─────────────────────────────────────────────────────
function PhotoGrid({ photos, onPick, col }) {
  if (photos.length === 0)
    return (
      <Typography sx={{ color: col.text, opacity: 0.6, textAlign: "center", py: 4 }}>
        Aucune photo disponible.
      </Typography>
    );
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(5, 1fr)" },
      gap: 1,
    }}>
      {photos.map((img) => (
        <Box
          key={img.ID_media}
          onClick={() => onPick(img.ID_media)}
          sx={{ cursor: "pointer", borderRadius: 1, overflow: "hidden", "&:hover img": { opacity: 0.75 } }}
        >
          <Box
            component="img"
            src={getMediaUrl(img.public_id)}
            alt={img.label || "photo"}
            loading="lazy"
            sx={{ width: "100%", height: 100, objectFit: "cover", display: "block", transition: "opacity 0.15s" }}
          />
        </Box>
      ))}
    </Box>
  );
}

// ─── Onglet Albums ─────────────────────────────────────────────────────────────
function AlbumTab({ fetchAlbums, onPick, col }) {
  const [albums, setAlbums] = useState(null);
  const [openAlbum, setOpenAlbum] = useState(null); // { name, photos }
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  useEffect(() => {
    setLoadingAlbums(true);
    fetchAlbums()
      .then(setAlbums)
      .catch(() => setAlbums([]))
      .finally(() => setLoadingAlbums(false));
  }, []); // eslint-disable-line

  const handleOpenAlbum = async (album) => {
    setLoadingPhotos(true);
    try {
      const data = await getAlbum(album.album_id);
      const photos = (data.medias || []).filter((m) => !m.mime_type?.startsWith("video/"));
      setOpenAlbum({ name: album.name, photos });
    } catch {
      setOpenAlbum({ name: album.name, photos: [] });
    } finally {
      setLoadingPhotos(false);
    }
  };

  if (loadingAlbums)
    return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: col.text }} /></Box>;

  if (openAlbum) {
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton size="small" onClick={() => setOpenAlbum(null)} sx={{ color: col.text }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ color: col.text, fontWeight: 600 }}>{openAlbum.name}</Typography>
        </Box>
        {loadingPhotos
          ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: col.text }} /></Box>
          : <PhotoGrid photos={openAlbum.photos} onPick={onPick} col={col} />
        }
      </>
    );
  }

  if (!albums || albums.length === 0)
    return (
      <Typography sx={{ color: col.text, opacity: 0.6, textAlign: "center", py: 4 }}>
        Aucun album disponible.
      </Typography>
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {albums.map((album) => (
        <Box
          key={album.album_id}
          onClick={() => handleOpenAlbum(album)}
          sx={{
            p: 1.5, borderRadius: 1, cursor: "pointer",
            border: `1px solid ${col.text}22`,
            "&:hover": { backgroundColor: col.text + "11" },
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <Typography sx={{ color: col.text }}>{album.name}</Typography>
          <Typography sx={{ color: col.text, opacity: 0.4, fontSize: "0.8rem" }}>
            {album.media_count != null ? `${album.media_count} média(s)` : ""}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Dialog picker ─────────────────────────────────────────────────────────────
function CoverPickerDialog({ open, onClose, onPick, theme }) {
  const col = theme.custom.mymodal;
  const [tab, setTab] = useState(0);
  const [gallery, setGallery] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(false);

  useEffect(() => {
    if (!open || tab !== 0 || gallery !== null) return;
    setLoadingGallery(true);
    getMyMedia()
      .then((all) => setGallery(all.filter((m) => !m.mime_type?.startsWith("video/"))))
      .catch(() => setGallery([]))
      .finally(() => setLoadingGallery(false));
  }, [open, tab, gallery]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) { setTab(0); setGallery(null); }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: col.main, border: col.border } }}
    >
      <DialogTitle sx={{ color: col.text, display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0 }}>
        Choisir une photo de fond
        <IconButton onClick={onClose} sx={{ color: col.text }}><CloseIcon /></IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 2,
          "& .MuiTab-root": { color: col.text + "88", textTransform: "none" },
          "& .Mui-selected": { color: col.text },
          "& .MuiTabs-indicator": { backgroundColor: col.button },
        }}
      >
        <Tab label="Galerie" />
        <Tab label="Mes albums" />
        <Tab label="Partagés avec moi" />
      </Tabs>

      <DialogContent>
        {tab === 0 && (
          loadingGallery
            ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: col.text }} /></Box>
            : <PhotoGrid photos={gallery || []} onPick={onPick} col={col} />
        )}
        {tab === 1 && (
          <AlbumTab key="my" fetchAlbums={getMyAlbums} onPick={onPick} col={col} />
        )}
        {tab === 2 && (
          <AlbumTab key="shared" fetchAlbums={getSharedWithMe} onPick={onPick} col={col} />
        )}
      </DialogContent>
    </Dialog>
  );
}

const POS_KEY = (id) => `cover_pos_${id}`;

function loadPosition(coverId) {
  if (!coverId) return { x: 50, y: 50 };
  try {
    const s = localStorage.getItem(POS_KEY(coverId));
    if (s) return JSON.parse(s);
  } catch {}
  return { x: 50, y: 50 };
}

// ─── Page Home ─────────────────────────────────────────────────────────────────
export default function Home() {
  const { state: user, setState: setUser } = useContext(AuthContext);
  const theme = useTheme();
  const col = theme.custom.mymodal;
  const { showSnack, Snack } = useSnack();

  const coverInputRef = useRef(null);
  const containerRef = useRef(null);
  const dragRef = useRef(null);          // { startX, startY, startPosX, startPosY }
  const posRef = useRef({ x: 50, y: 50 }); // toujours à jour (évite stale closure)

  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [repositionMode, setRepositionMode] = useState(false);
  const [coverPosition, setCoverPositionState] = useState({ x: 50, y: 50 });

  const setCoverPosition = useCallback((pos) => {
    posRef.current = pos;
    setCoverPositionState(pos);
  }, []);

  // Charger la position sauvegardée quand la couverture change
  useEffect(() => {
    setCoverPosition(loadPosition(user?.users_cover_id));
    setRepositionMode(false);
  }, [user?.users_cover_id, setCoverPosition]);

  const coverSrc = user?.users_cover_public_id ? getMediaUrl(user.users_cover_public_id) : null;

  // ── Drag ──
  const startDrag = useCallback((clientX, clientY) => {
    dragRef.current = {
      startX: clientX, startY: clientY,
      startPosX: posRef.current.x, startPosY: posRef.current.y,
    };
  }, []);

  const onDrag = useCallback((clientX, clientY) => {
    if (!dragRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    const newX = Math.min(100, Math.max(0, dragRef.current.startPosX - (dx / rect.width) * 100));
    const newY = Math.min(100, Math.max(0, dragRef.current.startPosY - (dy / rect.height) * 100));
    setCoverPosition({ x: newX, y: newY });
  }, [setCoverPosition]);

  const endDrag = useCallback(() => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (user?.users_cover_id) {
      localStorage.setItem(POS_KEY(user.users_cover_id), JSON.stringify(posRef.current));
    }
  }, [user?.users_cover_id]);

  useEffect(() => {
    if (!repositionMode) return;
    const onMouseMove = (e) => onDrag(e.clientX, e.clientY);
    const onMouseUp = () => endDrag();
    const onTouchMove = (e) => { e.preventDefault(); onDrag(e.touches[0].clientX, e.touches[0].clientY); };
    const onTouchEnd = () => endDrag();
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [repositionMode, onDrag, endDrag]);

  // ── Handlers upload / picker ──
  const handleCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { user: updated } = await uploadCover(file);
      setUser(updated);
      showSnack("Fond d'écran mis à jour !", "success");
    } catch (err) {
      showSnack(err.message || "Erreur upload", "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handlePickFromGallery = async (mediaId) => {
    setPickerOpen(false);
    setUploading(true);
    try {
      const updated = await setCoverFromGallery(mediaId);
      setUser(updated);
      showSnack("Fond d'écran mis à jour !", "success");
    } catch (err) {
      showSnack(err.message || "Erreur mise à jour", "error");
    } finally {
      setUploading(false);
    }
  };

  const btnSx = {
    backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
    color: "#fff", fontSize: "0.75rem", textTransform: "none",
    border: "1px solid rgba(255,255,255,0.3)",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
  };

  return (
    <>
      <Header />

      {user ? (
        <Box
          ref={containerRef}
          sx={{
            position: "relative", minHeight: "calc(100vh - 64px)", overflow: "hidden",
            cursor: repositionMode ? (dragRef.current ? "grabbing" : "grab") : "default",
            userSelect: repositionMode ? "none" : "auto",
          }}
          onMouseDown={repositionMode ? (e) => startDrag(e.clientX, e.clientY) : undefined}
          onTouchStart={repositionMode ? (e) => startDrag(e.touches[0].clientX, e.touches[0].clientY) : undefined}
        >
          {/* ── Fond d'écran ── */}
          {coverSrc ? (
            <Box
              component="img"
              src={coverSrc}
              alt=""
              draggable={false}
              sx={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "cover",
                objectPosition: `${coverPosition.x}% ${coverPosition.y}%`,
                zIndex: 0, pointerEvents: "none",
                transition: dragRef.current ? "none" : "object-position 0.1s ease",
              }}
            />
          ) : (
            <Box sx={{
              position: "absolute", inset: 0, zIndex: 0,
              background: `linear-gradient(135deg, ${col.main} 0%, ${col.button}55 100%)`,
            }} />
          )}

          {/* ── Voile sombre ── */}
          {coverSrc && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 1, backgroundColor: "rgba(0,0,0,0.35)", pointerEvents: "none" }} />
          )}

          {/* ── Bonjour (haut gauche, hors mode reposition) ── */}
          {!repositionMode && (
            <Box sx={{ position: "relative", zIndex: 2, p: 3, pointerEvents: "none" }}>
              <Typography variant="h5" sx={{ color: coverSrc ? "#fff" : col.text, textShadow: coverSrc ? "0 1px 8px rgba(0,0,0,0.7)" : "none" }}>
                Bonjour, {user.users_pseudo} !
              </Typography>
            </Box>
          )}

          {/* ── Hint centré en mode reposition ── */}
          {repositionMode && (
            <Box sx={{
              position: "absolute", inset: 0, zIndex: 2,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}>
              <Typography sx={{ color: "#fff", opacity: 0.8, fontSize: "0.85rem", textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>
                Glisse l'image pour repositionner
              </Typography>
            </Box>
          )}

          {/* ── Boutons (toujours en bas à droite, z-index au-dessus du mode reposition) ── */}
          <Box sx={{ position: "absolute", bottom: 16, right: 16, zIndex: 10, display: "flex", gap: 1 }}>
            {repositionMode ? (
              <Button
                size="small" variant="contained"
                startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                onClick={() => { endDrag(); setRepositionMode(false); }}
                sx={{ ...btnSx, border: "1px solid rgba(255,255,255,0.6)" }}
              >
                Valider
              </Button>
            ) : (
              <>
                <Button size="small" component="label" variant="contained"
                  startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <WallpaperIcon sx={{ fontSize: 16 }} />}
                  disabled={uploading} sx={btnSx}
                >
                  Depuis l'appareil
                  <input hidden type="file" accept="image/*" onChange={handleCoverFile} ref={coverInputRef} />
                </Button>
                <Button size="small" variant="contained"
                  startIcon={<WallpaperIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setPickerOpen(true)} disabled={uploading} sx={btnSx}
                >
                  Depuis mes photos
                </Button>
                {coverSrc && (
                  <Button size="small" variant="contained"
                    startIcon={<OpenWithIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setRepositionMode(true)} disabled={uploading} sx={btnSx}
                  >
                    Repositionner
                  </Button>
                )}
              </>
            )}
          </Box>

          <CoverPickerDialog
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onPick={handlePickFromGallery}
            theme={theme}
          />
        </Box>
      ) : (
        <Box sx={{
          minHeight: "calc(100vh - 64px)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          p: 4, color: col.text,
        }}>
          <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
            Bienvenue sur Family Share.
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Ce site est là pour nous permettre de partager nos souvenirs{" "}
            <br />
            photos, vidéos... à conserver, à partager avec 1 seule personne ou
            toute la famille,
            <br />
            à vous de choisir et de faire vivre cet espace ensemble 😽
            <br />
          </Typography>
        </Box>
      )}

      {Snack}
    </>
  );
}
